/**
 * 顾问陪跑 · 客户列表（草案 A-1 / A-3 / A-4 转交）。
 *
 * 销售工作台（A-1「每天打开就知道该跟进谁」）：每位客户列出名下每本书的阶段、已聊进度、最近讲述，
 * 并标出哪些事在等你动手；可按阶段 / 待跟进类型 / N 天没讲筛选。筛选条件同步在地址栏里，
 * 首页「今日待跟进」点进来直接带着条件。
 *
 * 销售：只看到自己名下的客户（后端强制），可建档。
 * 超管：看全部客户与归属销售、合伙人交叉标记；可代某位销售建档、批量转交（离职交接）。
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Button, Card, Checkbox, Form, Input, Modal, Progress, Select, Space, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../../components/PermissionGate';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../contracts/permissions';
import {
  createAdvisorCustomer,
  listAdvisorCustomers,
  listAdvisorSales,
  transferAdvisorCustomers,
  type AdvisorCustomerSummaryDto,
  type Attention,
  type BookStage,
} from '../../../api/advisor';
import { CustomerFields, customerToPayload, type CustomerFormValues } from './profile-form';
import {
  ATTENTION_META,
  ATTENTION_ORDER,
  IDLE_DAY_OPTIONS,
  STAGE_META,
  STAGE_OPTIONS,
  bookTitle,
  lastNarratedText,
  progressPercent,
} from './stage';

const { Text } = Typography;

function formatTs(ms: number | null): string {
  if (ms == null) return '-';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

const PAGE_SIZE = 20;

export default function AdvisorCustomersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = usePermission(PERMISSIONS.ADVISOR_MANAGE);

  // 筛选条件放在地址栏：首页「今日待跟进」点进来、刷新页面、复制链接给同事，条件都不丢
  const [params, setParams] = useSearchParams();
  const keyword = params.get('keyword') ?? '';
  const overlapOnly = params.get('overlapOnly') === '1';
  const stage = (STAGE_OPTIONS as string[]).includes(params.get('stage') ?? '') ? (params.get('stage') as BookStage) : undefined;
  const attention = (ATTENTION_ORDER as string[]).includes(params.get('attention') ?? '')
    ? (params.get('attention') as Attention)
    : undefined;
  const idleDays = IDLE_DAY_OPTIONS.includes(Number(params.get('idleDays'))) ? Number(params.get('idleDays')) : undefined;
  const advisorAdminId = params.get('advisorAdminId') ?? undefined;
  const page = Math.max(1, parseInt(params.get('page') ?? '1', 10) || 1);
  const filtered = !!(stage || attention || idleDays);

  /** 改一个或几个条件；改了条件就回到第一页 */
  const setFilter = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };
  const [selected, setSelected] = useState<string[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [createForm] = Form.useForm<CustomerFormValues & { advisorAdminId?: string }>();
  const [transferForm] = Form.useForm<{ toAdminId: string; reason?: string }>();

  const listQuery = useQuery({
    queryKey: ['advisor-customers', { keyword, overlapOnly, stage, attention, idleDays, advisorAdminId, page }],
    queryFn: () =>
      listAdvisorCustomers({ keyword, overlapOnly, stage, attention, idleDays, advisorAdminId, page, pageSize: PAGE_SIZE }),
  });

  const salesQuery = useQuery({
    queryKey: ['advisor-sales'],
    queryFn: listAdvisorSales,
    enabled: canManage,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['advisor-customers'] });

  const createMutation = useMutation({
    mutationFn: (values: CustomerFormValues & { advisorAdminId?: string }) =>
      createAdvisorCustomer({
        ...customerToPayload(values),
        ...(values.advisorAdminId ? { advisorAdminId: values.advisorAdminId } : {}),
      }),
    onSuccess: (data) => {
      message.success('已建档');
      setCreateOpen(false);
      createForm.resetFields();
      refresh();
      navigate(`/advisor/customers/${encodeURIComponent(data.customerId)}`);
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '建档失败');
    },
  });

  const transferMutation = useMutation({
    mutationFn: transferAdvisorCustomers,
    onSuccess: (data) => {
      const skipped = data.skipped.length ? `，${data.skipped.length} 位未转（已属于对方或已被转走）` : '';
      message.success(`已转交 ${data.transferred.length} 位客户${skipped}`);
      setTransferOpen(false);
      transferForm.resetFields();
      setSelected([]);
      refresh();
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '转交失败');
    },
  });

  const columns: ColumnsType<AdvisorCustomerSummaryDto> = [
    {
      title: '客户',
      dataIndex: 'displayName',
      render: (name: string, row) => (
        <Space direction="vertical" size={2}>
          <Space size={4}>
            <Typography.Link strong onClick={() => navigate(`/advisor/customers/${encodeURIComponent(row.customerId)}`)}>
              {name}
            </Typography.Link>
            {row.partnerOverlap && (
              <Tooltip title={`合伙人 ${row.partnerOverlap.partnerId} 也有归属，佣金待你判定`}>
                <Tag color="orange">合伙人交叉</Tag>
              </Tooltip>
            )}
          </Space>
          {row.contactPhone && <Text type="secondary">{row.contactPhone}</Text>}
          {row.attention.length > 0 && (
            <Space size={2} wrap>
              {row.attention.map((a) => (
                <Tooltip key={a} title={ATTENTION_META[a].hint}>
                  <Tag color={ATTENTION_META[a].color} style={{ marginInlineEnd: 0 }}>
                    {ATTENTION_META[a].label}
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          )}
        </Space>
      ),
    },
    {
      // 每本书一行：阶段 + 讲述人 + 已聊进度。给爸妈各做一本就是两行
      title: '书与进度',
      render: (_, row) =>
        row.books.length === 0 ? (
          <Text type="secondary">还没有订单</Text>
        ) : (
          <Space direction="vertical" size={4}>
            {row.books.map((b) => (
              <Space key={b.advisorOrderId} size={6} wrap data-book-row={b.advisorOrderId}>
                <Tag color={STAGE_META[b.stage].color} style={{ marginInlineEnd: 0 }}>
                  {STAGE_META[b.stage].label}
                </Tag>
                <Text>{bookTitle(b) || '未填讲述人'}</Text>
                {b.progress && (
                  <Tooltip title={`已聊 ${b.progress.discussed}/${b.progress.total} 题（近似，以详情页「问题清单与进度」为准）`}>
                    <Space size={4}>
                      <Progress percent={progressPercent(b.progress)} size="small" showInfo={false} style={{ width: 80, margin: 0 }} />
                      <Text type="secondary">
                        {b.progress.discussed}/{b.progress.total}
                      </Text>
                    </Space>
                  </Tooltip>
                )}
              </Space>
            ))}
          </Space>
        ),
    },
    {
      title: '最近讲述',
      render: (_, row) => (
        <Text type={row.attention.includes('idle') ? 'danger' : undefined}>
          {row.books.some((b) => b.workId) ? lastNarratedText(row.lastActiveAt) : '-'}
        </Text>
      ),
    },
    ...(canManage
      ? [{ title: '归属销售', dataIndex: 'advisorNickname', render: (v: string | null) => v ?? '-' }]
      : []),
    { title: '最近更新', dataIndex: 'updatedAt', render: (v: number) => formatTs(v) },
  ];

  return (
    <Card
      title={canManage ? '顾问客户' : '我的客户'}
      extra={
        <Space>
          {canManage && (
            <Button disabled={selected.length === 0} onClick={() => setTransferOpen(true)}>
              转交{selected.length ? `（${selected.length}）` : ''}
            </Button>
          )}
          <PermissionGate point={PERMISSIONS.ADVISOR_CUSTOMER_WRITE}>
            <Button type="primary" onClick={() => setCreateOpen(true)}>
              新建客户
            </Button>
          </PermissionGate>
        </Space>
      }
    >
      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          allowClear
          defaultValue={keyword}
          key={keyword}
          placeholder="搜索客户称呼 / 讲述人 / 电话"
          onSearch={(v) => setFilter({ keyword: v.trim() || undefined })}
          style={{ width: 260 }}
        />
        <Select
          allowClear
          placeholder="阶段"
          value={stage}
          style={{ width: 130 }}
          onChange={(v) => setFilter({ stage: v })}
          options={STAGE_OPTIONS.map((v) => ({ value: v, label: STAGE_META[v].label }))}
        />
        <Select
          allowClear
          placeholder="待跟进"
          value={attention}
          style={{ width: 160 }}
          onChange={(v) => setFilter({ attention: v })}
          options={ATTENTION_ORDER.map((v) => ({ value: v, label: ATTENTION_META[v].label }))}
        />
        <Select
          allowClear
          placeholder="几天没讲"
          value={idleDays}
          style={{ width: 130 }}
          onChange={(v) => setFilter({ idleDays: v ? String(v) : undefined })}
          options={IDLE_DAY_OPTIONS.map((v) => ({ value: v, label: `${v} 天以上没讲` }))}
        />
        {canManage && (
          <Checkbox checked={overlapOnly} onChange={(e) => setFilter({ overlapOnly: e.target.checked ? '1' : undefined })}>
            只看合伙人交叉
          </Checkbox>
        )}
        {advisorAdminId && (
          <Tag closable onClose={() => setFilter({ advisorAdminId: undefined })}>
            只看一位销售的客户
          </Tag>
        )}
      </Space>
      {filtered && listQuery.data?.truncated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="客户很多，按阶段 / 待跟进筛选时只扫描了最近更新的 1000 位。可以先用关键词缩小范围。"
        />
      )}

      <Table<AdvisorCustomerSummaryDto>
        rowKey="customerId"
        scroll={{ x: 'max-content' }}
        loading={listQuery.isLoading}
        dataSource={listQuery.data?.items ?? []}
        columns={columns}
        rowSelection={canManage ? { selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) } : undefined}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: listQuery.data?.total ?? 0,
          onChange: (p) => setFilter({ page: p > 1 ? String(p) : undefined }),
          showTotal: (t) => `共 ${t} 位`,
        }}
        locale={{ emptyText: '还没有客户，点右上角「新建客户」开始' }}
      />

      <Modal
        title="新建客户"
        open={createOpen}
        width={560}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        okText="建档"
        confirmLoading={createMutation.isPending}
        destroyOnHidden
      >
        <Form form={createForm} layout="vertical" onFinish={(v) => createMutation.mutate(v)} preserve={false}>
          {canManage && (
            <Form.Item name="advisorAdminId" label="归属销售" tooltip="不选则建在你自己名下">
              <Select
                allowClear
                loading={salesQuery.isLoading}
                options={(salesQuery.data ?? []).map((s) => ({ value: s.id, label: s.nickname }))}
              />
            </Form.Item>
          )}
          <CustomerFields />
          <Alert
            type="info"
            showIcon
            message="讲述人和背景属于每本书：建档后在客户详情里开单时填写，给爸妈各做一本就各开一单。"
          />
        </Form>
      </Modal>

      <Modal
        title={`转交 ${selected.length} 位客户`}
        open={transferOpen}
        onCancel={() => setTransferOpen(false)}
        onOk={() => transferForm.submit()}
        okText="确认转交"
        confirmLoading={transferMutation.isPending}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="转交后原销售立即看不到这些客户，新销售能看到全部历史（含订单与转交记录）。"
        />
        <Form
          form={transferForm}
          layout="vertical"
          preserve={false}
          onFinish={(v) => transferMutation.mutate({ customerIds: selected, toAdminId: v.toAdminId, reason: v.reason })}
        >
          <Form.Item name="toAdminId" label="接收的销售" rules={[{ required: true, message: '请选择接收人' }]}>
            <Select
              loading={salesQuery.isLoading}
              options={(salesQuery.data ?? []).map((s) => ({ value: s.id, label: s.nickname }))}
            />
          </Form.Item>
          <Form.Item name="reason" label="原因">
            <Input placeholder="如：离职交接" maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
